import { useEffect } from "react";
import { useSocket } from "@/providers/socket-provider";
import { useDealsListStore } from "@/store/deals-list-store";
import { useHasInProgressReports } from "@/hooks/use-has-in-progress-reports";

interface ReportStatusUpdatePayload {
  companyId: string;
  // BE names this opportunityId but it carries the workspace id (see
  // webhooks.ts / report-status-poller.ts which emit to `workspace:<id>`).
  opportunityId: string;
  reportStatus: Record<string, string>;
  changedField: string;
  newStatus: string;
}

const POLL_FALLBACK_INTERVAL_MS = 5000;

/**
 * Keeps the report-status indicator fresh for EVERY deal in the list, not just
 * the one open on the detail page (which useReportStatusSocket already covers).
 *
 * The workspace socket room is joined app-wide by useWorkspaceSocket, so every
 * `report:status_update` for the workspace already arrives here — we just patch
 * the matching deal in the list store. Without this, a row's spinner is frozen
 * at the last fetchDeals snapshot and never clears when a report finishes while
 * the user is sitting on the list.
 */
export function useDealsListReportStatusSocket(
  workspaceId?: string | null
) {
  const { socket, isConnected, fallbackToPolling } = useSocket();
  const hasInProgress = useHasInProgressReports();

  useEffect(() => {
    if (!socket || !isConnected || !workspaceId) return;

    const handler = (data: ReportStatusUpdatePayload) => {
      if (data.opportunityId !== workspaceId) return;
      // `_id` is the report_status subdocument id, not a report field.
      if (data.changedField === "_id") return;

      const { deals, updateDeal } = useDealsListStore.getState();
      const existing = deals[data.companyId];
      // Brand-new deals arrive via fetchDeals; don't synthesise a partial entry.
      if (!existing) return;

      const prevStatus =
        existing.reportStatus?.opportunityApplication?.report_status ?? {};
      updateDeal(data.companyId, {
        reportStatus: {
          opportunityApplication: {
            report_status: { ...prevStatus, [data.changedField]: data.newStatus },
          },
        },
      });
    };

    socket.on("report:status_update", handler);
    return () => {
      socket.off("report:status_update", handler);
    };
  }, [socket, isConnected, workspaceId]);

  // Fallback for a fully-down socket: refetch the list while any report is
  // running so spinners still resolve. Stops as soon as nothing is in progress.
  useEffect(() => {
    if (!fallbackToPolling || !hasInProgress || !workspaceId) return;
    const id = setInterval(() => {
      // Refresh rather than fetch: a plain fetch asks for the whole workspace,
      // which would replace the user's filtered or curated table with every
      // deal in it on every poll tick.
      // Handles its own errors (logs + sets store error state).
      // Silent: this runs on a timer, and putting the table into its loading
      // state every few seconds would make it flicker while reports run.
      void useDealsListStore
        .getState()
        .refreshDeals(workspaceId, { silent: true });
    }, POLL_FALLBACK_INTERVAL_MS);
    return () => clearInterval(id);
  }, [fallbackToPolling, hasInProgress, workspaceId]);
}
